import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiClient, apiRequest, ApiError } from '../services/online/apiClient';

const STORAGE_KEY = 'online_auth';

const AuthContext = createContext(null);

function loadStoredSession() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function persistSession(session) {
    try {
        if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        else localStorage.removeItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private mode, storage full, etc.) — the
        // session just won't survive a reload, nothing else to do about it.
    }
}

/**
 * Session/auth state for the online backend (RONDA_ONLINE.md Fase 9).
 * Persists {user, accessToken, refreshToken} to localStorage so a reload
 * doesn't force a fresh login. On mount, an existing session is validated
 * (and its profile refreshed) via GET /users/me before isLoading clears —
 * screens gate on isLoading to avoid a login-screen flash while that check
 * is in flight.
 */
export function AuthProvider({ children }) {
    const [session, setSession] = useState(loadStoredSession);
    const [isLoading, setIsLoading] = useState(true);
    const sessionRef = useRef(session);
    sessionRef.current = session;
    const refreshInFlightRef = useRef(null);

    const clearSession = useCallback(() => {
        setSession(null);
        persistSession(null);
    }, []);

    const updateSession = useCallback((next) => {
        setSession(next);
        persistSession(next);
    }, []);

    /**
     * Authenticated request with a single automatic refresh-and-retry on a
     * 401 (TOKEN_EXPIRED/TOKEN_INVALID/UNAUTHORIZED) — matches the backend's
     * refresh contract exactly: POST /auth/refresh returns only a new
     * accessToken, the refreshToken is never rotated. TOKEN_INVALID is what
     * authenticateToken.ts actually returns for an expired access token
     * (TOKEN_EXPIRED there means "revoked", not "expired") — treating only
     * TOKEN_EXPIRED as recoverable left an expired session neither refreshed
     * nor cleared, so protected screens rendered instead of redirecting to
     * login. If the refresh itself fails, the session is cleared so
     * protected screens redirect to login.
     */
    const authFetch = useCallback(
        async (path, options = {}) => {
            const current = sessionRef.current;
            if (!current) throw new ApiError('Not authenticated', 'UNAUTHORIZED', 401);

            try {
                return await apiRequest(path, { ...options, token: current.accessToken });
            } catch (err) {
                const isAuthError =
                    err instanceof ApiError &&
                    (err.code === 'TOKEN_EXPIRED' || err.code === 'TOKEN_INVALID' || err.code === 'UNAUTHORIZED');
                if (!isAuthError) throw err;

                try {
                    // Concurrent 401s (e.g. several requests on app resume) share
                    // one in-flight refresh instead of each firing its own.
                    if (!refreshInFlightRef.current) {
                        refreshInFlightRef.current = apiClient
                            .post('/auth/refresh', { refreshToken: current.refreshToken })
                            .then(({ accessToken }) => {
                                // Don't resurrect a session that was logged out mid-refresh.
                                if (sessionRef.current) updateSession({ ...sessionRef.current, accessToken });
                                return accessToken;
                            })
                            .finally(() => {
                                refreshInFlightRef.current = null;
                            });
                    }
                    const accessToken = await refreshInFlightRef.current;
                    return await apiRequest(path, { ...options, token: accessToken });
                } catch {
                    clearSession();
                    throw err;
                }
            }
        },
        [updateSession, clearSession]
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!sessionRef.current) {
                setIsLoading(false);
                return;
            }
            try {
                const user = await authFetch('/users/me');
                if (!cancelled) updateSession({ ...sessionRef.current, user });
            } catch {
                // authFetch already cleared the session on an unrecoverable auth failure.
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount only
    }, []);

    const login = useCallback(
        async (email, password) => {
            const result = await apiClient.post('/auth/login', { email, password });
            updateSession(result);
            return result.user;
        },
        [updateSession]
    );

    const register = useCallback(
        async (name, email, password) => {
            const result = await apiClient.post('/auth/register', { name, email, password });
            updateSession(result);
            return result.user;
        },
        [updateSession]
    );

    const logout = useCallback(async () => {
        const current = sessionRef.current;
        clearSession();
        if (current) {
            try {
                // Sends refreshToken too so the backend revokes both halves
                // of this device's session — see auth.service.ts's logout().
                await apiRequest('/auth/logout', {
                    method: 'POST',
                    token: current.accessToken,
                    body: { refreshToken: current.refreshToken },
                });
            } catch {
                // Best-effort: the local session is already cleared either way.
            }
        }
    }, [clearSession]);

    // Revokes every session of this user, on every device — the "I think my
    // account is compromised" escape hatch. Unlike logout(), this must
    // succeed server-side before the local session clears, so the caller
    // can surface a failure instead of silently no-op-ing.
    const logoutAllDevices = useCallback(async () => {
        const current = sessionRef.current;
        if (!current) return;
        await apiRequest('/auth/logout-all', { method: 'POST', token: current.accessToken });
        clearSession();
    }, [clearSession]);

    // Completes the native OAuth deep-link flow (rondaybaciga://oauth2/callback):
    // the backend has already issued a fresh token pair by the time the app
    // is reopened with them, so this just fetches the profile and stores
    // the session — no /auth/login call involved.
    const loginWithTokens = useCallback(
        async (accessToken, refreshToken) => {
            const user = await apiRequest('/users/me', { token: accessToken });
            updateSession({ user, accessToken, refreshToken });
            return user;
        },
        [updateSession]
    );

    // Reads the freshest token at call time (not a snapshot) — for the
    // WebSocket client (Fase 9b), which needs the raw token for its
    // connection URL and re-reads this on every (re)connect attempt, so a
    // token refreshed meanwhile (via authFetch, elsewhere) is picked up too.
    const getAccessToken = useCallback(() => sessionRef.current?.accessToken ?? null, []);

    // Merges a fresh user object (e.g. the result of PATCH /users/me) into
    // the stored session, so profile edits show up immediately everywhere
    // without a full page reload.
    const updateUser = useCallback(
        (user) => {
            const current = sessionRef.current;
            if (!current) return;
            updateSession({ ...current, user });
        },
        [updateSession]
    );

    const value = {
        user: session?.user ?? null,
        isAuthenticated: Boolean(session),
        isLoading,
        login,
        register,
        logout,
        logoutAllDevices,
        loginWithTokens,
        authFetch,
        getAccessToken,
        updateUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
}
